/**
 * ActieLijst — Homescreen actiepunten widget
 * Toont wat de schrijnwerker vandaag moet doen, automatisch
 * samengesteld vanuit offertes, facturen, projecten.
 * Tailwind CSS — past in het bestaande DashboardPage patroon.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  Clock, AlertTriangle, Zap, CreditCard, Calendar,
  ArrowRight, RefreshCw, CheckCircle2
} from 'lucide-react'
import api from '../../services/api'

// ── Configuratie per actietype ────────────────────────────────
const ACTIE_CONFIG = {
  OFFERTE_WACHT_ANTWOORD: {
    icon: Clock,
    kleur: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    accent: 'border-l-orange-500',
    btnBg: 'bg-orange-600 hover:bg-orange-700',
  },
  OFFERTE_VERLOOPT: {
    icon: AlertTriangle,
    kleur: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    accent: 'border-l-red-500',
    btnBg: 'bg-red-600 hover:bg-red-700',
  },
  SNELOFFERTE_OMZETTEN: {
    icon: Zap,
    kleur: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    accent: 'border-l-blue-500',
    btnBg: 'bg-blue-600 hover:bg-blue-700',
  },
  FACTUUR_ONBETAALD: {
    icon: CreditCard,
    kleur: 'text-rose-700',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    accent: 'border-l-rose-600',
    btnBg: 'bg-rose-600 hover:bg-rose-700',
  },
  PROJECT_DEADLINE: {
    icon: Calendar,
    kleur: 'text-indigo-600',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    accent: 'border-l-indigo-500',
    btnBg: 'bg-indigo-600 hover:bg-indigo-700',
  },
}

const URGENTIE_STYLE = {
  KRITIEK: { label: 'Dringend', cls: 'bg-red-100 text-red-700' },
  HOOG:    { label: 'Vandaag',  cls: 'bg-orange-100 text-orange-700' },
  NORMAAL: { label: 'Opvolgen', cls: 'bg-blue-100 text-blue-700' },
}

function fmtEuro(n) {
  if (n == null) return null
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(n)
}

// ── Begroeting ────────────────────────────────────────────────
function Begroeting({ naam }) {
  const uur = new Date().getHours()
  const groet = uur < 12 ? 'Goedemorgen' : uur < 18 ? 'Goedemiddag' : 'Goedenavond'
  const datum = new Date().toLocaleDateString('nl-BE', {
    weekday: 'long', day: 'numeric', month: 'long',
  })

  return (
    <div className="mb-4">
      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
        {groet}{naam ? `, ${naam}` : ''}
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
        {datum.charAt(0).toUpperCase() + datum.slice(1)}
      </p>
    </div>
  )
}

// ── Samenvatting tellers ──────────────────────────────────────
function SamenvattingBalk({ punten }) {
  const { t } = useTranslation()
  const kritiek = punten.filter(p => p.urgentie === 'KRITIEK').length
  const hoog = punten.filter(p => p.urgentie === 'HOOG').length
  const normaal = punten.filter(p => p.urgentie === 'NORMAAL').length

  if (punten.length === 0) return null

  return (
    <div className="grid grid-cols-3 gap-3 mb-4">
      {kritiek > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">{kritiek}</div>
          <div className="text-xs text-red-600 dark:text-red-400">Dringend</div>
        </div>
      )}
      {hoog > 0 && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{hoog}</div>
          <div className="text-xs text-orange-600 dark:text-orange-400">{t('dashboard.actieLijst.today')}</div>
        </div>
      )}
      {normaal > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{normaal}</div>
          <div className="text-xs text-blue-600 dark:text-blue-400">Opvolgen</div>
        </div>
      )}
    </div>
  )
}

// ── Leeg scherm ───────────────────────────────────────────────
function LeegScherm() {
  const { t } = useTranslation()
  return (
    <div className="text-center py-10 px-6 bg-green-50 dark:bg-green-900/10 rounded-xl border border-dashed border-green-300 dark:border-green-800">
      <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
      <div className="text-lg font-semibold text-green-700 dark:text-green-400">
        {t('dashboard.actieLijst.allGood')}
      </div>
      <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
        {t('dashboard.actieLijst.noActions')}
      </div>
    </div>
  )
}

// ── Actiekaart ────────────────────────────────────────────────
function ActieKaart({ punt, onActie }) {
  const config = ACTIE_CONFIG[punt.type] || ACTIE_CONFIG.SNELOFFERTE_OMZETTEN
  const urg = URGENTIE_STYLE[punt.urgentie] || URGENTIE_STYLE.NORMAAL
  const Icon = config.icon

  return (
    <div className={`bg-white dark:bg-gray-800 border ${config.border} dark:border-gray-700 border-l-4 ${config.accent} rounded-r-xl p-3.5 mb-2.5 flex items-start gap-3`}>
      {/* Icoon */}
      <div className={`${config.bg} rounded-lg w-9 h-9 flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-4.5 h-4.5 ${config.kleur}`} />
      </div>

      {/* Tekst */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${urg.cls}`}>
            {urg.label}
          </span>
          {punt.datum_info && (
            <span className="text-[11px] text-gray-400 dark:text-gray-500">{punt.datum_info}</span>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
          {punt.titel}
        </div>
        {(punt.subtitel || punt.bedrag != null) && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {punt.subtitel}
            {punt.bedrag != null && (
              <span className={`font-semibold ${config.kleur} ml-2`}>
                {fmtEuro(punt.bedrag)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Actieknop */}
      <button
        onClick={() => onActie(punt)}
        className={`flex-shrink-0 ${config.btnBg} text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors flex items-center gap-1`}
      >
        {punt.actie_label || 'Bekijken'}
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  )
}

// ── Hoofd component ───────────────────────────────────────────
export default function ActieLijst({ gebruikerNaam }) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: punten = [], isLoading, error, refetch } = useQuery({
    queryKey: ['actielijst'],
    queryFn: async () => {
      const res = await api.get('/actielijst/')
      return res.data
    },
    refetchInterval: 5 * 60 * 1000, // 5 minuten
    staleTime: 60 * 1000,
  })

  const handleActie = useCallback((punt) => {
    if (punt.actie_url) navigate(punt.actie_url)
  }, [navigate])

  const kritiekPunten = punten.filter(p => p.urgentie === 'KRITIEK')
  const hoogPunten = punten.filter(p => p.urgentie === 'HOOG')
  const normaalPunten = punten.filter(p => p.urgentie === 'NORMAAL')

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-5 mb-6">
      <Begroeting naam={gebruikerNaam} />

      {/* Snelofferte knop */}
      <button
        onClick={() => navigate('/snelofferte/nieuw')}
        className="w-full mb-5 px-4 py-3 bg-gray-900 dark:bg-gray-700 text-white rounded-xl text-sm font-bold hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
      >
        <Zap className="w-4 h-4" />
        Snelofferte maken
      </button>

      {/* Laden */}
      {isLoading && (
        <div className="text-center py-8 text-gray-400 text-sm">
          <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
          {t('dashboard.actieLijst.loading')}
        </div>
      )}

      {/* Fout */}
      {error && !isLoading && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 flex items-center justify-between">
          <span className="text-sm text-red-600 dark:text-red-400">{t('dashboard.actieLijst.loadFailed')}</span>
          <button onClick={() => refetch()} className="text-sm text-red-600 dark:text-red-400 underline">
            Opnieuw
          </button>
        </div>
      )}

      {!isLoading && !error && (
        <>
          <SamenvattingBalk punten={punten} />

          {punten.length === 0 && <LeegScherm />}

          {kritiekPunten.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider mb-2">
                {t('dashboard.actieLijst.urgentHeader')}
              </div>
              {kritiekPunten.map(p => (
                <ActieKaart key={p.id} punt={p} onActie={handleActie} />
              ))}
            </div>
          )}

          {hoogPunten.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wider mb-2">
                Hoge prioriteit
              </div>
              {hoogPunten.map(p => (
                <ActieKaart key={p.id} punt={p} onActie={handleActie} />
              ))}
            </div>
          )}

          {normaalPunten.length > 0 && (
            <div className="mb-4">
              <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-2">
                Opvolgen
              </div>
              {normaalPunten.map(p => (
                <ActieKaart key={p.id} punt={p} onActie={handleActie} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
